import { ApiError } from '../../api/client';

/**
 * Ce que lit la page Tenant (ADR-128) : `GetTenantProfile`, `GetTenantComposition`, `GetTenantParameters`.
 * Les identifiants internes servent aux commandes ; la page ne les affiche jamais.
 */
export type LocalizedText = Readonly<Record<string, string>>;
export type Address = { lines?: string[]; postal_code?: string; city?: string; region?: string; country: string };
export type Warning = { field: string; code: string; message: string };
export type VocabularyEntry = { value: string; label?: LocalizedText };

export interface TenantProfile {
  tenant: { id: string; version: number; code?: string; display_name: string; default_locale?: string; status: string; environment: string };
  parameters: Record<string, unknown>;
  entities: EntityRow[];
  sites: SiteRow[];
  establishments: EstablishmentRow[];
  unattached_locations: Array<{ code: string; display_name: string }>;
  unattached_site_locations: Array<{ id: string; code: string; display_name: string }>;
  vocabulary: { roles: VocabularyEntry[]; approval_kinds: VocabularyEntry[]; site_kinds: VocabularyEntry[] };
}

export interface EntityRow {
  id: string;
  version: number;
  code: string;
  display_name: string;
  legal_name?: string;
  legal_form?: string;
  legal_identifiers?: Record<string, string>;
  activity_code?: string;
  activity_label?: string;
  head_office_address?: Address;
  status: string;
  /** ADR-134 : société cessée au profit d'une autre. */
  ceased_on?: string;
  successor_entity_id?: string;
  is_default: boolean;
  warnings: Warning[];
}

export interface SiteRow {
  id: string;
  version: number;
  code: string;
  display_name: string;
  site_kind?: string;
  address?: Address;
  timezone?: string;
  gln?: string;
  entity_ids: string[];
  status: string;
  /** ADR-134 : site fermé à une date. */
  closed_on?: string;
  closure_reason?: string;
  root_location?: { code: string; display_name: string };
  location_count: number;
}

export interface EstablishmentRow {
  id: string;
  version: number;
  entity_id: string;
  site_id: string;
  display_name?: string;
  legal_identifiers?: Record<string, string>;
  roles?: string[];
  approval_numbers?: Record<string, string>;
  opened_on?: string;
  closed_on?: string;
  closure_reason?: string;
  status: string;
  warnings: Warning[];
}

export interface CompositionLayer {
  lineage: string;
  layer: 'core' | 'pack' | 'customer' | 'solution';
  kind: 'core' | 'pack' | 'extension' | 'customer' | 'solution';
  label?: LocalizedText;
  description?: LocalizedText;
  version: string;
  dependencies: Array<{ lineage: string; range: string }>;
  published_at?: string;
  published_by?: string;
  replaces?: { lineage: string; range: string; version?: string };
  previous: Array<{ version: string; status: string; published_at?: string; published_by?: string }>;
  previous_count: number;
}

export const text = (value: LocalizedText | undefined, fallback = ''): string => (value === undefined ? fallback : (value['fr'] ?? Object.values(value)[0] ?? fallback));

export const formatAddress = (address: Address | undefined): string =>
  address === undefined ? '' : [...(address.lines ?? []), [address.postal_code, address.city].filter((part) => part !== undefined && part !== '').join(' ')].filter((part) => part !== '').join(', ');

/** SIREN et SIRET lus par groupes (803 843 176 00014) ; les autres identifiants tels quels. */
export function formatIdentifier(key: string, value: string | undefined): string {
  if (value === undefined || value === '') return '—';
  if (key === 'siren' && /^[0-9]{9}$/.test(value)) return value.replace(/^(\d{3})(\d{3})(\d{3})$/, '$1 $2 $3');
  if (key === 'siret' && /^[0-9]{14}$/.test(value)) return value.replace(/^(\d{3})(\d{3})(\d{3})(\d{5})$/, '$1 $2 $3 $4');
  return value;
}

export const formatDate = (iso: string | undefined): string => (iso === undefined ? '—' : new Date(iso).toLocaleDateString('fr-FR'));

/** Message d'erreur d'une commande, tel que le serveur le dit, avec ses champs. */
export function errorMessage(caught: unknown): { message: string; elevation: boolean } {
  if (caught instanceof ApiError) return { message: [caught.message, ...caught.fieldErrors.map((entry) => entry.message)].join(' '), elevation: caught.category === 'POLICY_REJECTION' };
  return { message: String(caught), elevation: false };
}

export const LOCALES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
];

/**
 * Texte montré aux utilisateurs : les renvois de conception (« (ADR-063) », « (spec Food §41) ») ne les
 * concernent pas et sont ôtés à l'affichage (remarque de l'utilisateur du 2026-10-14).
 */
export function forUsers(value: string): string {
  return value
    .replace(/\s*\((?:[^()]*\b(?:ADR-[0-9A-Z]+|spec|§)[^()]*)\)/gi, '')
    .replace(/\s+([.,;:])/g, '$1')
    .trim();
}
